// services/team.service.ts
import axios from "axios";
import { API_URL } from "../config/config";
import { getHeaderOptions } from "./getHeaderOptions";
import { Logger } from '../utils/logger';
import { TeamMember, Team, Role, PaginationParams } from '../types/team.types';

// Following your existing service pattern
const API_ENDPOINT = API_URL + 'api/v1';

// Team Member request params interface
interface TeamMemberRequestParams {
  orderBy?: string;
  page?: number;
  perPage?: number;
  status?: string;
  isAscending?: boolean;
  search?: string;
  role?: string;
  teams?: string;
  byOrganization?: boolean;
}

// Team request params interface  
interface TeamRequestParams {
  orderBy?: string;
  page?: number;
  perPage?: number;
  search?: string;
}

export const getAllTeamMembers = (params: TeamMemberRequestParams) => {
  Logger.log('TeamService: Making getAllTeamMembers request with params:', params);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || '' // Remove Bearer - getHeaderOptions likely adds it
  };
  return axios.get(`${API_ENDPOINT}/user/employee`, { headers, params });
};

/**
 * Get subordinate employees
 * Equivalent to getSubOrdinateEmployee from mobile team.api.ts
 */
export const getSubordinateEmployees = (params: TeamMemberRequestParams) => {
  Logger.log('TeamService: Making getSubordinateEmployees request with params:', params);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.get(`${API_ENDPOINT}/user/subordinate-employee`, { headers, params });
};

/**
 * Create a new team member
 * Equivalent to createEmployee from mobile team.api.ts
 */
export const createTeamMember = (data: Partial<TeamMember>) => {
  Logger.log('TeamService: Creating team member:', data);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.post(`${API_ENDPOINT}/user/employee`, data, { headers });
};

/**
 * Update an existing team member
 * Equivalent to updateEmployee from mobile team.api.ts
 */
export const updateTeamMember = (data: Partial<TeamMember>) => {
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };

  // Extract ID for the API endpoint
  const id = data._id;
  
  // Remove _id from request body to prevent issues
  const { _id, ...updatePayload } = data;
  
  Logger.log('TeamService: Updating team member with ID:', id);
  Logger.log('TeamService: Update payload (without _id):', updatePayload);
  
  return axios.put(`${API_ENDPOINT}/user/employee/${id}`, updatePayload, { headers });
};

/**
 * Delete/deactivate a team member
 */
export const deleteTeamMember = (id: string) => {
  Logger.log('TeamService: Deleting team member with ID:', id);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.delete(`${API_ENDPOINT}/user/employee/${id}`, { headers });
};

/**
 * Get team member by ID
 */
export const getTeamMemberById = (id: string) => {
  Logger.log('TeamService: Fetching team member by ID:', id);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.get(`${API_ENDPOINT}/user/employee/${id}`, { headers });
};

/**
 * Get all teams with pagination
 * Equivalent to getAllTeams from mobile team.api.ts
 */
export const getAllTeams = (params: TeamRequestParams) => {
  Logger.log('TeamService: Making getAllTeams request with params:', params);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.get(`${API_ENDPOINT}/team`, { headers, params });
};

/**
 * Get team by ID
 * Equivalent to getTeamById from mobile team.api.ts
 */
export const getTeamById = (id: string) => {
  Logger.log('TeamService: Fetching team by ID:', id);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.get(`${API_ENDPOINT}/team/${id}`, { headers });
};

/**
 * Create a new team
 * Equivalent to createTeam from mobile team.api.ts
 */
export const createTeam = (data: Partial<Team>) => {
  Logger.log('TeamService: Creating team:', data);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.post(`${API_ENDPOINT}/team`, data, { headers });
};

/**
 * Update an existing team
 * Equivalent to updateTeam from mobile team.api.ts
 */
export const updateTeam = (data: Partial<Team>) => {
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };

  // Extract ID for the API endpoint
  const id = data._id;
  
  // Remove _id from request body to prevent issues
  const { _id, ...updatePayload } = data;
  
  Logger.log('TeamService: Updating team with ID:', id);
  Logger.log('TeamService: Update payload (without _id):', updatePayload);
  
  return axios.put(`${API_ENDPOINT}/team/${id}`, updatePayload, { headers });
};

/**
 * Delete a team
 * Equivalent to deleteTeamById from mobile team.api.ts
 */
export const deleteTeam = (id: string) => {
  Logger.log('TeamService: Deleting team with ID:', id);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.delete(`${API_ENDPOINT}/team/${id}`, { headers });
};

/**
 * Get all roles
 * Equivalent to getAllRoles from mobile team.api.ts
 */
export const getAllRoles = () => {
  Logger.log('TeamService: Fetching all roles');
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.get(`${API_ENDPOINT}/role`, { headers });
};

/**
 * Create a new role
 * Equivalent to createRole from mobile team.api.ts
 */
export const createRole = (data: Partial<Role>) => {
  Logger.log('TeamService: Creating role:', data);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.post(`${API_ENDPOINT}/role`, data, { headers });
};

/**
 * Update a role
 * Equivalent to updateRole from mobile team.api.ts
 */
export const updateRole = (data: Partial<Role>) => {
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };

  // Extract ID for the API endpoint
  const id = data._id;

  // Remove _id from request body to prevent issues
  const { _id, ...updatePayload } = data;

  Logger.log('TeamService: Updating role with ID:', id);
  Logger.log('TeamService: Update payload (without _id):', updatePayload);

  return axios.put(`${API_ENDPOINT}/role/${id}`, updatePayload, { headers });
};

/**
 * Set user goals (separate API call for goals)
 * Mobile pattern: Goals are updated separately from user profile
 */
export const setUserGoal = (goals: Array<{
  type: 'sales_amount' | 'calls';
  target: number;
  frequency: 'monthly' | 'quarterly';
  userId: string;
}>) => {
  Logger.log('TeamService: Setting user goals:', goals);
  const headers = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem('auth_token') || ''
  };
  return axios.post(`${API_ENDPOINT}/user/goal`, goals, { headers });
};